import { useAuthManager } from '@doltech/core/lib/auth/authManager';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';
import { fromScreen, toScreen } from '@doltech/core/lib/responsive/responsive.util';
import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { fetchPublicBookLandingById } from '@doltech/modules/lib/book-seo-page/api';
import { BookSEOHeadingLoader } from '@doltech/modules/lib/book-seo-page/components/BookSEOHeading/BookSEOHeadingLoader';
import { RelatedBooksSwiper } from '@doltech/modules/lib/book-seo-page/components/BookSEOHeading/RelatedBooksSwiper';
import { BookSEOPageContext } from '@doltech/modules/lib/book-seo-page/components/BookSEOPageContext';
import { FullTestList } from '@doltech/modules/lib/book-seo-page/components/FullTestList/FullTestList';

import { BookDetail } from '@doltech/modules/lib/book-seo-page/types';
import { FABLandingHome } from '@doltech/modules/lib/landing-home/components/FABLandingHome';
import {
  BookSeriesCard,
  BookSeriesViewModeEnum,
} from '@doltech/modules/lib/page-management/landing/books/components/LandingBookSeries';
import { DynamicPageProps } from '@doltech/routeDefinition';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { ContactDol } from '@doltech/ui/lib/figma/ContactDol/ContactDol';
import { BookDetailPromotionLoader } from '@doltech/ui/lib/figma/Layout/Landing/BookDetailPromotionLoader';
import { LandingPromotionLoader } from '@doltech/ui/lib/figma/Layout/Landing/LandingPromotionLoader';
import { useLandingSharedDataStore } from '@doltech/ui/lib/figma/Layout/Landing/model/landing-shared-data.hook';
import { LandingLayoutDefault } from '@doltech/ui/lib/figma/Layout/Landing/PremadeLayout/LandingLayoutDefault';
import { PromotionBarShareBanner } from '@doltech/ui/lib/figma/Layout/Landing/ui/PromotionBarShareBanner/PromotionBarShareBanner';
import { Link } from '@doltech/ui/lib/figma/Link';
import { useFromScreens } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { ExtraInfoView } from '@doltech/ui/lib/shared/components/ExtraInfo/ExtraInfoView';
import { GridContentV2 } from '@doltech/ui/lib/shared/components/PageSectionStructure/Content/GridContentV2';
import { PageSectionStructureV2 } from '@doltech/ui/lib/shared/components/PageSectionStructure/PageSectionStructureV2';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { JuniorPromotionBanner } from '@doltech/ui/lib/figma/Layout/Landing/ui/JuniorPromotionBanner/JuniorPromotionBanner';

interface BookSEOPageTemplateProps
  extends React.HTMLAttributes<HTMLDivElement>,
    Pick<DynamicPageProps, 'seoData'> {
  bookDetail: BookDetail;
  relatedBooks: BookDetail[];
  featuredBooks: BookDetail[];
}

export const BookSEOPageTemplate = observer((props: BookSEOPageTemplateProps) => {
  const { bookDetail, relatedBooks, featuredBooks, seoData, ...rest } = props;
  const { extraInfo } = seoData;
  const auth = useAuthManager();
  const { isLoggedIn } = useGetAuthInfo();
  const store = useLandingSharedDataStore();
  const [, from1366] = useFromScreens([0, 1366]);

  const { data: bookLandingDetail } = useQuery(
    [isLoggedIn, bookDetail.id, 'detail'],
    async () => {
      const accessToken = await auth.getAccessToken();
      return fetchPublicBookLandingById(bookDetail.id, accessToken);
    },
    {
      enabled: Boolean(isLoggedIn),
    }
  );

  const renderSubHeader = React.useCallback(() => {
    return (
      <SubHeaderWrapper>
        <Link href={store.pageLink?.staticPageLink?.LANDING_HOME?.url || '/'}>Trang chủ</Link>
        <IconChevronChevronRight fontSize={16} color="primary100" />
        <Link href={store.pageLink?.staticPageLink?.LANDING_FULL_TEST.url}>IELTS Online Test</Link>
        <BookNameWrapper>
          <IconChevronChevronRight fontSize={16} color="primary100" />
          <span className="book-name">{bookDetail.name}</span>
        </BookNameWrapper>
      </SubHeaderWrapper>
    );
  }, [
    bookDetail.name,
    store.pageLink?.staticPageLink?.LANDING_FULL_TEST.url,
    store.pageLink?.staticPageLink?.LANDING_HOME?.url,
  ]);

  const relatedBooksExceptCurrentBook = relatedBooks.filter((book) => book.id !== bookDetail.id);

  return (
    <BookSEOPageContext.Provider
      value={{
        bookDetail: bookLandingDetail ?? bookDetail,
        relatedBooks,
        featuredBooks,
      }}
    >
      <JuniorPromotionBanner />
      <Layout seoData={seoData} {...rest}>
        <ContactDol />
        <FABLandingHome />
        <PromotionBarShareBanner />
        <Wrapper>
          <Container>
            <SubHeader>{renderSubHeader()}</SubHeader>
            <BookLayout>
              <MainContent>
                <BookOverview>
                  <RelatedBooksSwiper activeBookId={bookDetail.id} />
                  <div className="content">
                    <BookSEOHeadingLoader className="book-info" />
                    <FullTestList />
                    {Boolean(extraInfo) && (
                      <ExtraInfoView extraInfo={extraInfo} compact fontInter />
                    )}
                  </div>
                </BookOverview>

                {!from1366 && <LandingPromotionLoader />}

                {bookDetail.bookSeriesIds === 'Official Guide To Ielts' ||
                bookDetail.bookSeriesIds === 'Ielts Trainer' ? (
                  <PageSectionStructureV2
                    oldStyling
                    title="Bộ sách IELTS nổi bật"
                    content={
                      <GridContentV2
                        data={featuredBooks}
                        renderCard={(book) => (
                          <BookSeriesCard
                            book={book}
                            viewMode={
                              BookSeriesViewModeEnum.MORE_THAN_SIX_BOOKS_WITHOUT_MOBILE_SWIPER
                            }
                            isHorizontalCard={true}
                            withCardDescription={false}
                          />
                        )}
                        gridColumn={[
                          {
                            breakpoint: 480,
                            numOfColumn: 1,
                          },
                          {
                            breakpoint: 768,
                            numOfColumn: 2,
                          },
                        ]}
                      />
                    }
                  />
                ) : (
                  <PageSectionStructureV2
                    oldStyling
                    title={`Sách cùng bộ ${bookDetail.bookSeriesIds}`}
                    content={
                      <GridContentV2
                        data={relatedBooksExceptCurrentBook}
                        renderCard={(book) => (
                          <BookSeriesCard
                            book={book}
                            viewMode={
                              BookSeriesViewModeEnum.MORE_THAN_SIX_BOOKS_WITHOUT_MOBILE_SWIPER
                            }
                            isHorizontalCard={true}
                            withCardDescription={false}
                          />
                        )}
                        gridColumn={[
                          {
                            breakpoint: 642,
                            numOfColumn: 1,
                          },
                          {
                            breakpoint: 768,
                            numOfColumn: 2,
                          },
                        ]}
                      />
                    }
                  />
                )}
              </MainContent>

              {from1366 && <BookDetailPromotionLoader />}
            </BookLayout>
          </Container>
        </Wrapper>
      </Layout>
    </BookSEOPageContext.Provider>
  );
});

const SubHeader = styled.div`
  max-width: 720px;
  margin: 0 auto 24px;

  ${fromScreen(1366)} {
    margin: 0 0 32px;
  }
`;
const BookOverview = styled.div`
  .content {
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    gap: 32px;
  }
  .related-books-swiper {
    margin-bottom: 24px;
  }
  ${fromScreen(768)} {
    .related-books-swiper {
      margin-bottom: 32px;
    }
  }
`;

const Layout = styled(LandingLayoutDefault)``;

const SubHeaderWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, max-content));
  column-gap: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  align-items: center;
  color: ${colorsV2.primary100};
  .book-name {
    color: ${colorsV2.black80};
  }
`;

const BookNameWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  column-gap: 8px;
  align-items: center;
  ${toScreen(479)} {
    display: none;
  }
`;

const Wrapper = styled.div`
  padding: 32px 16px;

  ${fromScreen(768)} {
    padding: 32px 24px 100px;
  }
`;

const BookLayout = styled.div`
  ${fromScreen(1366)} {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    gap: 32px;
  }
`;

const MainContent = styled.div`
  max-width: 720px;
  margin: 0 auto;

  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  gap: 60px;

  ${fromScreen(768)} {
    gap: 100px;
  }
  .section-promotion-expired-short-no,
  .section-promotion-almost-expired-short-no {
    .content {
      grid-template-columns: 404px minmax(0, 1fr);
    }
  }
`;

const Container = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;
